import { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../lib/convexApi";
import { AddSecretModal } from "./AddSecretModal";

// Convex shape — listSecrets returns metadata only; ciphertext / iv /
// authTag never leave the server. The "Reveal" interaction from the
// legacy REST UI has been removed in this PR — vault decrypt is now an
// internalAction (api.vault.actions.getDecryptedSecret) that only
// other Convex functions (LLM proxy, mint-token, etc.) can call. A
// future public revealSecret action with its own admin-step-up + audit
// flow is tracked under PR 8 of the Convex migration.

export interface VaultSecretRow {
  _id: string;
  name: string;
  createdBy: string;
  createdAt: number;
  lastUsedAt?: number;
}

export function CredentialsVaultTab() {
  const rawSecrets = useQuery((api as any).vault.queries.listSecrets, {});
  const setSecret = useAction((api as any).vault.actions.setSecret);
  const revokeSecret = useAction((api as any).vault.actions.revokeSecret);

  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyName, setBusyName] = useState<string | null>(null);

  const loading = rawSecrets === undefined;
  const secrets: VaultSecretRow[] = (rawSecrets ?? []) as VaultSecretRow[];

  async function handleDelete(secret: VaultSecretRow) {
    if (!window.confirm(`Delete secret "${secret.name}"? This cannot be undone.`)) {
      return;
    }
    setBusyName(secret.name);
    setError(null);
    try {
      await revokeSecret({ name: secret.name });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to revoke secret";
      setError(`Failed to delete ${secret.name}: ${msg}`);
    } finally {
      setBusyName(null);
    }
  }

  return (
    <div className="ac-vault-tab">
      <div className="ac-tab-header">
        <div>
          <h2 className="ac-tab-title">Credentials vault</h2>
          <p className="ac-tab-subtitle">
            Encrypted secrets for your agents. Stored with AES-256-GCM (AAD-bound per org+name);
            decrypt is internal-only.
          </p>
        </div>
        <button
          className="ac-btn ac-btn--primary"
          onClick={() => setShowAdd(true)}
          aria-label="Add secret"
        >
          + Add secret
        </button>
      </div>

      {error && <p className="ac-error-banner">{error}</p>}

      {loading ? (
        <p className="ac-loading">Loading secrets…</p>
      ) : secrets.length === 0 ? (
        <div className="ac-empty-state">
          <p>
            No secrets yet. Add your first secret to inject it into agent environments at runtime.
          </p>
        </div>
      ) : (
        <table className="ac-vault-table" aria-label="Credentials vault secrets">
          <thead>
            <tr>
              <th className="ac-vault-row__name">Name</th>
              <th className="ac-vault-row__masked">Value</th>
              <th className="ac-vault-row__used-by">Last used</th>
              <th className="ac-vault-row__actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {secrets.map((secret) => (
              <tr key={secret._id} className="ac-vault-row">
                <td className="ac-vault-row__name">{secret.name}</td>
                <td className="ac-vault-row__masked">
                  <span className="ac-vault-masked-value">••••••••••••••••</span>
                </td>
                <td className="ac-vault-row__used-by">
                  {secret.lastUsedAt ? (
                    new Date(secret.lastUsedAt).toLocaleString()
                  ) : (
                    <span className="ac-dim">—</span>
                  )}
                </td>
                <td className="ac-vault-row__actions">
                  <button
                    className="ac-btn ac-btn--danger ac-btn--sm"
                    onClick={() => handleDelete(secret)}
                    disabled={busyName === secret.name}
                    aria-label={`Delete ${secret.name}`}
                  >
                    {busyName === secret.name ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddSecretModal
          setSecret={setSecret}
          onClose={() => setShowAdd(false)}
          onCreated={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}
